/**
 * AdminCreditAuditPanel
 *
 * CC-211-AUDIT Phase 2I: Platform-Admin-only panel for the Privacy KPI
 * Dashboard. Accepts a member UUID, fetches the member's recent Credit
 * Completeness snapshot history via the Phase 2G admin endpoints, and
 * opens a full-screen lineage modal (`CreditAuditLineageModal`) on row
 * click.
 *
 * Access gate (client mirror of server): 'Platform Admin' role only.
 * Server guard is authoritative — client gate is UX only, not security.
 */

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FiSearch, FiAlertTriangle, FiExternalLink, FiClock } from 'react-icons/fi';
import useHasRole from '../../hooks/useHasRole';
import {
  getCreditCompletenessHistory,
  CreditCompletenessSnapshotSummary,
  AdminCreditAuditError,
  isValidMemberUuid,
} from '../../services/adminCreditAuditService';
import CreditAuditLineageModal from './CreditAuditLineageModal';

const HISTORY_LIMIT = 10;

function readQueryParam(key: string): string {
  return new URLSearchParams(window.location.search).get(key) || '';
}

function writeQueryParams(patch: Record<string, string | null>): void {
  const params = new URLSearchParams(window.location.search);
  Object.entries(patch).forEach(([k, v]) => {
    if (v === null || v === '') params.delete(k);
    else params.set(k, v);
  });
  const next = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', next);
}

const AdminCreditAuditPanel: FC = () => {
  const hasAccess = useHasRole(['Platform Admin']);

  const [memberIdInput, setMemberIdInput] = useState(() => readQueryParam('memberId'));
  const [resolvedMemberId, setResolvedMemberId] = useState<string>(() => {
    const v = readQueryParam('memberId');
    return isValidMemberUuid(v) ? v : '';
  });
  const [snapshots, setSnapshots] = useState<CreditCompletenessSnapshotSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openSnapshotId, setOpenSnapshotId] = useState<string>(() => readQueryParam('snapshotId'));

  const inputValid = useMemo(() => isValidMemberUuid(memberIdInput), [memberIdInput]);

  const fetchHistory = useCallback(async (memberId: string) => {
    setLoading(true);
    setError(null);
    setSnapshots(null);
    try {
      const resp = await getCreditCompletenessHistory(memberId, HISTORY_LIMIT);
      setSnapshots(resp.snapshots);
    } catch (err) {
      const msg = err instanceof AdminCreditAuditError
        ? (err.status === 404 ? 'Member not found' : err.reason || err.message)
        : err instanceof Error ? err.message : 'Failed to load history';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAccess && resolvedMemberId) fetchHistory(resolvedMemberId);
  }, [hasAccess, resolvedMemberId, fetchHistory]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = memberIdInput.trim();
    if (!isValidMemberUuid(trimmed)) {
      setError('Please enter a valid member UUID (36 characters, hex + dashes).');
      return;
    }
    setResolvedMemberId(trimmed);
    writeQueryParams({ memberId: trimmed, snapshotId: null });
  };

  const openSnapshot = (snapshotId: string) => {
    setOpenSnapshotId(snapshotId);
    writeQueryParams({ snapshotId });
  };

  const closeSnapshot = () => {
    setOpenSnapshotId('');
    writeQueryParams({ snapshotId: null });
  };

  if (!hasAccess) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
        <FiAlertTriangle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Restricted</h3>
        <p className="text-sm text-slate-600">This panel is available to <span className="font-semibold">Platform Admin</span> users only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div className="flex-1 w-full">
            <label htmlFor="cc-audit-member-id" className="block text-xs font-medium text-slate-700 mb-1">
              Member UUID
            </label>
            <input
              id="cc-audit-member-id"
              type="text"
              value={memberIdInput}
              onChange={(e) => setMemberIdInput(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <button
            type="submit"
            disabled={!inputValid || loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <FiSearch size={14} />
            {loading ? 'Loading…' : 'Load Snapshots'}
          </button>
        </form>
        <p className="mt-2 text-xs text-slate-500">
          All requests are logged with purpose <span className="font-mono">compliance_audit</span>.
          No PII is displayed — only UUIDs and scoring metadata.
        </p>
      </section>

      <HistoryTable
        loading={loading}
        error={error}
        resolvedMemberId={resolvedMemberId}
        snapshots={snapshots}
        onOpen={openSnapshot}
      />

      {openSnapshotId && resolvedMemberId && (
        <CreditAuditLineageModal
          isOpen={true}
          memberId={resolvedMemberId}
          snapshotId={openSnapshotId}
          onClose={closeSnapshot}
        />
      )}
    </div>
  );
};

export default AdminCreditAuditPanel;

// ============================================================================
// Subcomponents
// ============================================================================

interface HistoryTableProps {
  loading: boolean;
  error: string | null;
  resolvedMemberId: string;
  snapshots: CreditCompletenessSnapshotSummary[] | null;
  onOpen: (snapshotId: string) => void;
}

const HistoryTable: FC<HistoryTableProps> = ({ loading, error, resolvedMemberId, snapshots, onOpen }) => {
  if (!resolvedMemberId && !loading && !error) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
        <FiClock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        <p className="text-sm text-slate-600">Enter a member UUID above to load their recent Credit Completeness snapshots.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
        <p className="text-sm text-slate-600">Loading snapshot history…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
        <FiAlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-red-800">
          <p className="font-medium">Could not load history</p>
          <p className="mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (snapshots && snapshots.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <p className="text-sm text-slate-600">No Credit Completeness snapshots found for this member.</p>
      </div>
    );
  }

  if (!snapshots) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-xs text-slate-600">
        Showing {snapshots.length} most recent snapshot{snapshots.length === 1 ? '' : 's'} (newest first)
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Created</th>
              <th className="px-4 py-2 text-right">Overall</th>
              <th className="px-4 py-2 text-right">Coverage</th>
              <th className="px-4 py-2 text-right">Profile</th>
              <th className="px-4 py-2 text-left">Model</th>
              <th className="px-4 py-2 text-left">Pipeline</th>
              <th className="px-4 py-2 text-left">Input Hash</th>
              <th className="px-4 py-2 text-left">Trace</th>
              <th className="px-4 py-2 text-right">Reasons</th>
              <th className="px-4 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {snapshots.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-semibold text-slate-900">{s.overall_score.toFixed(2)}</td>
                <td className="px-4 py-2 text-right">{s.coverage_score.toFixed(2)}</td>
                <td className="px-4 py-2 text-right">{s.profile_score.toFixed(2)}</td>
                <td className="px-4 py-2 font-mono text-xs">{s.model_version}</td>
                <td className="px-4 py-2 font-mono text-xs">{s.scoring_pipeline_version}</td>
                <td className="px-4 py-2 font-mono text-xs">{s.input_hash_prefix}</td>
                <td className="px-4 py-2 font-mono text-xs">{s.trace_id ? s.trace_id.slice(0, 8) : '—'}</td>
                <td className="px-4 py-2 text-right">{s.reasons_count}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => onOpen(s.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View <FiExternalLink size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
